import { Conditions } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { client } from '@/client';
import { Account } from '@/client/accounts';
import { EmailType } from '@/client/system-emails';
import { Actions, Subjects } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { TranslationFunctionType } from '@/common/types';
import { DomainVerification } from '@/components/accounts/domain-verification';
import {
  useAccount,
  useDeleteSystemEmailTypeConfig,
  useSaveSystemEmailTypeConfig,
  useSystemEmailsTypesConfigs,
} from '@/hooks/query';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import {
  selectCurrentAccount,
  setCurrentAccount,
} from '@/store/features/account';
import { DialogType } from '@/system-settings/enums';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppIconTooltip } from '@/ui/icon-tooltip/AppIconTooltip';
import { InlineMenu } from '@/ui/inline-menu';
import { FlexContainer } from '@/ui/styled-ui';
import { getIsPhishingOnlyStatus } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { Checkbox } from 'primereact/checkbox';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { AccountHeader } from './AccountHeader';

const getPathItems = (
  account: Account | undefined,
  currentAccount: Account,
  can: (action: Actions, subject: Subjects) => boolean,
  t: TranslationFunctionType,
): MenuItem[] => {
  const pathItems: MenuItem[] = [];

  pathItems.push({
    label: currentAccount?.name,
    url: !currentAccount.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](currentAccount?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](currentAccount?.id),
    template: AppBreadCrumbTemplate,
  });

  if (
    currentAccount?.isSystem &&
    account &&
    can(Actions.READ, Subjects.ACCOUNTS)
  ) {
    pathItems.push(
      {
        label: t('accounts'),
        url: RedirectPaths[RedirectPathsEnum.ACCOUNTS](),
        template: AppBreadCrumbTemplate,
      },
      {
        label: account?.name,
        url: !currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
          : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
        className: 'active',
        template: AppBreadCrumbTemplate,
      },
    );
  }

  pathItems.push({
    label: t('account.emails'),
    url: currentAccount?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_EMAILS_AND_DELIVERY](
          account?.id as string,
        )
      : RedirectPaths[RedirectPathsEnum.ACCOUNT_EMAILS_AND_DELIVERY](),
    template: AppBreadCrumbTemplate,
  });

  return pathItems;
};

const StyledSection = styled.div<{ noBorder?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ noBorder }) =>
    !noBorder &&
    css`
      border-bottom: 1px solid var(--gray-dark);
      padding-bottom: var(--xlarge-padding);
    `}
`;

const StyledHeading = styled.h3`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
  color: var(--black-main);
  font-size: var(--semi-big-font-size);
  font-weight: 600;
  line-height: 1.5;
  margin: 0;
`;

const StyledDescription = styled.p`
  color: var(--black-main);
  font-size: var(--small-font-size);
  line-height: 1.5;
  margin: 0;
  margin-top: var(--xsmall-padding);
`;

const StyledCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
  margin-top: var(--medium-big-padding);
`;

const StyledCheckboxLabel = styled.label<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--xxsmall-padding);
  font-size: var(--small-font-size);
  cursor: pointer;
  font-weight: 500;
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      cursor: default;
    `}
`;

const StyledConfirmation = styled(FlexContainer)`
  flex-direction: column;
  align-items: flex-start;
  gap: var(--xxsmall-padding);
  list-style: disc;
  font-size: var(--small-font-size);
  line-height: 150%;

  p,
  ul {
    margin: 0;
  }

  ul {
    padding-left: var(--medium-padding);
  }

  li {
    padding-bottom: var(--xxsmall-padding);
  }

  li::marker {
    color: var(--red-main);
  }
`;

export const AccountEmailControlPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const toast = useToast();
  const { setDialogData } = useContext(DialogContext);
  const dispatch = useAppDispatch();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { can } = usePermission();
  const accountId = currentAccount?.isSystem ? id : currentAccount?.id;
  const {
    account,
    isLoading: isAccountLoading,
    refetch: refetchAccount,
  } = useAccount({
    accountId,
  });

  const { hash } = useLocation();
  const taskNotificationsSectionRef = useRef<HTMLDivElement>(null);
  const domainsSectionRef = useRef<HTMLDivElement>(null);

  const savedValuesRef = useRef({
    blockSystemEmails: false,
    adminTaskRemindersEnabled: true,
  });

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [blockSystemEmails, setBlockSystemEmails] = useState<boolean>(
    account?.blockSystemEmails ?? false,
  );
  const [isCheckboxLocked, setIsCheckboxLocked] = useState<boolean>(false);
  const [adminTaskRemindersEnabled, setAdminTaskRemindersEnabled] =
    useState<boolean>(true);

  const isFreeTrial = !!account?.freeTrialEndsAt;

  const { systemEmailsTypesConfigs, refetch: refetchConfigs } =
    useSystemEmailsTypesConfigs({
      take: 100,
      enabled: !!accountId && !isFreeTrial,
      filters: [
        {
          field: 'account',
          condition: Conditions.EQUAL,
          value: accountId,
        },
      ],
    });

  const { create: createConfig } = useSaveSystemEmailTypeConfig();
  const { delete: deleteConfig } = useDeleteSystemEmailTypeConfig();

  const adminTaskReminderConfig = systemEmailsTypesConfigs?.result?.find(
    (config) => config.type === EmailType.ADMIN_TASK_REQUIRED_REMINDER,
  );

  useEffect(() => {
    const enabled = adminTaskReminderConfig
      ? adminTaskReminderConfig.active !== false
      : true;
    savedValuesRef.current.adminTaskRemindersEnabled = enabled;
    setAdminTaskRemindersEnabled(enabled);
  }, [adminTaskReminderConfig]);

  const isDirty = useMemo(() => {
    const saved = savedValuesRef.current;
    return (
      blockSystemEmails !== saved.blockSystemEmails ||
      (!isFreeTrial &&
        adminTaskRemindersEnabled !== saved.adminTaskRemindersEnabled)
    );
  }, [blockSystemEmails, adminTaskRemindersEnabled, isFreeTrial]);

  const menuItems = [{ label: t('account.emails.system') }];

  if (!isFreeTrial) {
    menuItems.push({ label: t('account.emails.adminTaskReminders.sidebar') });
  }

  menuItems.push({ label: t('account.domains.title') });

  const sectionRefs: Record<number, React.RefObject<HTMLDivElement>> = {};
  menuItems.forEach((_, index) => {
    if (index === 0) return;
    const label = menuItems[index].label;
    if (label === t('account.emails.adminTaskReminders.sidebar')) {
      sectionRefs[index] = taskNotificationsSectionRef;
    } else if (label === t('account.domains.title')) {
      sectionRefs[index] = domainsSectionRef;
    }
  });

  const items = menuItems.map((item, index) => ({
    ...item,
    className: activeIndex === index ? 'active' : '',
    command: () => {
      setActiveIndex(index);
      sectionRefs[index]?.current?.scrollIntoView({ behavior: 'smooth' });
    },
  }));

  useEffect(() => {
    savedValuesRef.current.blockSystemEmails =
      account?.blockSystemEmails ?? false;
    setBlockSystemEmails(account?.blockSystemEmails ?? false);
    setIsCheckboxLocked(getIsPhishingOnlyStatus(account));
  }, [account]);

  useEffect(() => {
    // The domains section only mounts once the account has loaded, so the ref
    // is null on the initial render. Depend on isAccountLoading so this re-runs
    // after the section is committed (covers both fresh loads and cached data).
    if (hash !== '#domain-verification' || isAccountLoading) return;
    const domainsIndex = menuItems.length - 1;
    setActiveIndex(domainsIndex);
    domainsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [hash, isFreeTrial, isAccountLoading]);

  const handleSave = async () => {
    if (!account?.id) return false;
    setIsSubmitting(true);

    try {
      await client.accounts.updateAccount({
        accountId: account?.id,
        updates: { blockSystemEmails },
      });

      if (!isFreeTrial) {
        const configExists = !!adminTaskReminderConfig;
        const wasEnabled = adminTaskReminderConfig?.active !== false;

        if (adminTaskRemindersEnabled && !wasEnabled && configExists) {
          await deleteConfig(adminTaskReminderConfig.id);
        } else if (!adminTaskRemindersEnabled && !configExists) {
          await createConfig({
            type: EmailType.ADMIN_TASK_REQUIRED_REMINDER,
            sendTime: ['system'],
            active: false,
            account: account.id,
          });
        } else if (!adminTaskRemindersEnabled && configExists && wasEnabled) {
          await deleteConfig(adminTaskReminderConfig.id);
          await createConfig({
            type: EmailType.ADMIN_TASK_REQUIRED_REMINDER,
            sendTime: ['system'],
            active: false,
            account: account.id,
          });
        }

        refetchConfigs();
      }

      await refetchAccount();
      toast?.success(t('toast.success'), t('account.updated'));
      if (account?.id === currentAccount?.id) {
        const accountData = await client.accounts.getAccount(account.id);
        dispatch(setCurrentAccount(accountData));
      }
      return true;
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isAccountLoading || !account ? (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
        </FlexContainer>
      ) : (
        <>
          {currentAccount && (
            <AppBreadCrumb
              model={getPathItems(account, currentAccount, can, t)}
            />
          )}

          <AccountHeader
            selectedAccount={account}
            title={t('account.emails.system')}
            isDirty={isDirty}
            onSave={handleSave}
            isSaving={isSubmitting}
          />

          <FlexContainer
            gap="var(--big-padding)"
            justify="flex-start"
            align="flex-start"
            style={{ marginTop: 'var(--large-padding)' }}
          >
            <InlineMenu items={items} />
            <div style={{ flex: 1 }}>
              <StyledSection>
                <StyledHeading>
                  {t('account.emails.block.header')}
                </StyledHeading>
                <StyledDescription>
                  {t('account.emails.block.description')}
                </StyledDescription>
                <StyledCheckboxContainer>
                  <span
                    id="block-system-emails-row"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'var(--xsmall-padding)',
                    }}
                  >
                    <Checkbox
                      inputId="block-system-emails"
                      checked={blockSystemEmails}
                      disabled={isCheckboxLocked}
                      onChange={(e) => {
                        const checked = e.checked ?? false;
                        if (checked) {
                          setBlockSystemEmails(true);
                          return;
                        }
                        setDialogData({
                          show: true,
                          type: DialogType.WARNING,
                          header: t(
                            'dialog.account.emails.block.warning.heading',
                          ),
                          customContent: (
                            <StyledConfirmation>
                              <p>{t('dialog.account.emails.block.warning')}</p>
                              <ul>
                                <li
                                  dangerouslySetInnerHTML={{
                                    __html: t(
                                      'dialog.account.emails.block.warning.condition1',
                                    ),
                                  }}
                                />
                                <li
                                  dangerouslySetInnerHTML={{
                                    __html: t(
                                      'dialog.account.emails.block.warning.condition2',
                                    ),
                                  }}
                                />
                              </ul>
                            </StyledConfirmation>
                          ),
                          showDialogConfirmation: false,
                          confirmButtonLabel: t(
                            'dialog.account.emails.block.confirmButton',
                          ),
                          onAccept: () => setBlockSystemEmails(false),
                        });
                      }}
                    />
                    <StyledCheckboxLabel
                      htmlFor="block-system-emails"
                      disabled={isCheckboxLocked}
                    >
                      {t('account.emails.block.checkbox.label')}
                      {isCheckboxLocked ? (
                        <i
                          className="pi pi-info-circle ml-1"
                          style={{
                            display: 'flex',
                            fontSize: 'var(--xsmall-font-size)',
                            color: 'var(--gray-darker)',
                          }}
                        />
                      ) : (
                        <AppIconTooltip
                          target="block-system-emails-tooltip"
                          content={t('account.emails.block.checkbox.tooltip')}
                          position="right"
                        />
                      )}
                    </StyledCheckboxLabel>
                  </span>
                </StyledCheckboxContainer>
                {isCheckboxLocked && (
                  <Tooltip
                    target="#block-system-emails-row"
                    content={t('account.emails.block.checkbox.tooltip.locked')}
                    position="bottom"
                  />
                )}
              </StyledSection>

              {!isFreeTrial && (
                <StyledSection
                  ref={taskNotificationsSectionRef}
                  style={{ marginTop: 'var(--xlarge-padding)' }}
                >
                  <StyledHeading>
                    {t('account.emails.adminTaskReminders.header')}
                    <AppIconTooltip
                      target="admin-task-reminders-tooltip"
                      content={t(
                        'account.emails.adminTaskReminders.checkbox.tooltip',
                      )}
                      position="right"
                    />
                  </StyledHeading>
                  <StyledDescription>
                    {t('account.emails.adminTaskReminders.description')}
                  </StyledDescription>
                  <StyledCheckboxContainer>
                    <Checkbox
                      inputId="admin-task-reminders"
                      checked={adminTaskRemindersEnabled}
                      onChange={(e) => {
                        setAdminTaskRemindersEnabled(e.checked ?? false);
                      }}
                    />
                    <StyledCheckboxLabel htmlFor="admin-task-reminders">
                      {t('account.emails.adminTaskReminders.checkbox.label')}
                    </StyledCheckboxLabel>
                  </StyledCheckboxContainer>
                </StyledSection>
              )}

              <StyledSection
                ref={domainsSectionRef}
                noBorder
                style={{ marginTop: 'var(--xlarge-padding)' }}
              >
                <StyledHeading>
                  {t('account.domains.heading')}
                  <AppIconTooltip
                    target="domain-verification-tooltip"
                    content={t('account.domains.tooltip')}
                    position="right"
                  />
                </StyledHeading>
                <StyledDescription>
                  {t('account.domains.description')}
                </StyledDescription>
                <DomainVerification accountId={accountId as string} />
              </StyledSection>
            </div>
          </FlexContainer>
        </>
      )}
    </>
  );
};
